/**
 * AddFilterMenu component
 * Cascading menu for adding filters with searchable multi-select options
 * Fully accessible with keyboard navigation and screen reader support
 */

import type { SyntheticEvent } from 'react';
import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  MouseEvent,
  KeyboardEvent,
} from 'react';

import {
  Menu,
  MenuItem,
  ListItemText,
  ListSubheader,
  Popover,
  Button,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useTranslation } from 'react-i18next';

import styles from './AddFilterMenu.module.scss';
import { useGetConfigurationFeatures } from '@/api/REST/configuration-features';
import { useGetUserMe } from '@/api/REST/userMe';
import { FilterValueAutocomplete } from '@/components/filters/FilterValueAutocomplete';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import {
  useFacetOptions,
  useFacetCounts,
  useFacetOptionsByIds,
} from '@/hooks/filters';
import type { FacetOption } from '@/hooks/filters/types';
import type { FilterClause, FilterSet } from '@/lib/filter-dsl';
import {
  type ResourceConfig,
  type FacetConfig,
  getFacetsBySection,
} from '@/lib/resource-config';

// Debug flag: Set to true to visualize the safe triangle hover area
const DEBUG_SHOW_SAFE_TRIANGLE = false;

interface AddFilterMenuProps {
  config: ResourceConfig;
  filterSet?: FilterSet;
  /**
   * Optional search query to filter facet counts.
   * See FilterBar.tsx for rationale on why we use prop drilling instead of store.
   */
  searchQuery?: string;
  onAddFilter: (clause: FilterClause) => void;
}

type FacetConfigWithKey = FacetConfig & { key: string };

export const AddFilterMenu = ({
  config,
  filterSet = [],
  searchQuery,
  onAddFilter,
}: AddFilterMenuProps) => {
  // Fetch features directly - cached by React Query across all components
  const { data: features } = useGetConfigurationFeatures();
  const { data: userMeData } = useGetUserMe();
  const pagePermissions = useMemo(
    () => userMeData?.permittedPages?.map((p) => p.pageIdentifier) ?? [],
    [userMeData?.permittedPages],
  );
  const { t } = useTranslation('views');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const anchorPosition = useRef<{ top: number; left: number } | null>(null);
  const [selectedFacet, setSelectedFacet] = useState<FacetConfigWithKey | null>(
    null,
  );
  const [facetAnchorEl, setFacetAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedValues, setSelectedValues] = useState<FacetOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const hoverIntentTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const submenuBounds = useRef<DOMRect | null>(null);
  const [trianglePoints, setTrianglePoints] = useState<string | null>(null);

  const open = Boolean(anchorEl);
  const menuId = 'add-filter-menu';

  // Debounce search term to reduce API calls while typing
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
        setPage(0); // Reset pagination when search changes
      }, 300),
    [],
  );

  // Get options for the selected facet with server-side search
  const {
    data: paginatedOptions,
    isLoading,
    isFetching,
  } = useFacetOptions({
    config,
    facetKey: selectedFacet?.key || '',
    searchTerm: debouncedSearchTerm,
    page,
  });

  const options = paginatedOptions?.options || [];

  // Fetch facet counts for the selected facet
  const { data: counts } = useFacetCounts({
    config,
    filterSet,
    excludeFacet: selectedFacet?.key,
    query: searchQuery || undefined,
  });

  // Identify which selected values are missing from current options
  // (need to fetch their labels separately)
  const missingIds = useMemo(() => {
    if (!selectedFacet || selectedFacet.valueSource.kind !== 'collection') {
      return [];
    }

    const optionSlugs = new Set(options.map((opt) => opt.slug));
    return selectedValues
      .map((v) => v.slug)
      .filter((slug) => !optionSlugs.has(slug));
  }, [selectedValues, options, selectedFacet]);

  // Fetch missing options by their IDs
  const { data: missingOptions = [] } = useFacetOptionsByIds({
    config,
    facetKey: selectedFacet?.key || '',
    ids: missingIds,
  });

  // Merge fetched options with paginated options
  const allFetchedOptions = useMemo(() => {
    const optionsMap = new Map(options.map((opt) => [opt.slug, opt]));

    // Add missing options that were fetched by ID
    for (const opt of missingOptions) {
      if (!optionsMap.has(opt.slug)) {
        optionsMap.set(opt.slug, opt);
      }
    }

    return Array.from(optionsMap.values());
  }, [options, missingOptions]);

  // Merge counts into options
  const optionsWithCounts = useMemo(() => {
    if (!counts || !selectedFacet) return allFetchedOptions;
    const facetCounts = counts[selectedFacet.key] || {};

    return allFetchedOptions.map((opt) => {
      // Determine count key:
      // - If untranslatedLabel exists, use it (for translated facets with facetField like StatusName)
      // - If facetField exists but no untranslatedLabel, use label (for non-translated facets like CustomerName)
      // - Otherwise use slug (for facets without facetField)
      const countKey = opt.untranslatedLabel
        ? opt.untranslatedLabel
        : selectedFacet.facetField
          ? opt.label
          : opt.slug;
      const rawCount = facetCounts[countKey] || 0;
      return {
        ...opt,
        count: rawCount,
      };
    });
  }, [allFetchedOptions, counts, selectedFacet]);

  // For enum facets opted-in via hideEnumValuesWithoutMatches, drop values with
  // count === 0 so the dropdown only offers choices that would yield results.
  // Waits for counts to load before filtering to avoid pruning prematurely.
  const visibleOptions = useMemo(() => {
    if (
      !counts ||
      !selectedFacet ||
      selectedFacet.valueSource.kind !== 'enum' ||
      !selectedFacet.hideEnumValuesWithoutMatches
    ) {
      return optionsWithCounts;
    }
    return optionsWithCounts.filter((opt) => (opt.count ?? 0) > 0);
  }, [optionsWithCounts, counts, selectedFacet]);

  // Update submenu bounds when popover opens
  useEffect(() => {
    if (selectedFacet && popoverRef.current) {
      // Small delay to ensure popover is fully rendered
      const timer = setTimeout(() => {
        if (popoverRef.current) {
          submenuBounds.current = popoverRef.current.getBoundingClientRect();
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      submenuBounds.current = null;
    }
  }, [selectedFacet]);

  // Cleanup timeouts and debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.clear();
      if (hoverIntentTimeout.current) {
        clearTimeout(hoverIntentTimeout.current);
      }
      if (focusTimeout.current) {
        clearTimeout(focusTimeout.current);
      }
    };
  }, [debouncedSetSearch]);

  // Reset facet submenu state (search, selected values, page)
  const resetFacetSubmenu = useCallback(() => {
    debouncedSetSearch.clear();
    setSelectedFacet(null);
    setFacetAnchorEl(null);
    setSelectedValues([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPage(0);
  }, [debouncedSetSearch]);

  // Merge selected values with loaded options to ensure proper labels
  const selectedValuesWithLabels = useMemo(() => {
    const optionsMapById = new Map(
      allFetchedOptions.map((opt) => [opt.id, opt]),
    );
    const optionsMapBySlug = new Map(
      allFetchedOptions.map((opt) => [opt.slug, opt]),
    );

    return selectedValues.map((selected) => {
      // Try to find by id first (for real options)
      let loadedOption = optionsMapById.get(selected.id);

      // If not found by id, try to find by slug (for placeholder options)
      if (!loadedOption) {
        loadedOption = optionsMapBySlug.get(selected.slug);
      }

      return loadedOption || selected;
    });
  }, [selectedValues, allFetchedOptions]);

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    // Save the initial position so the menu doesn't move when filters are added
    anchorPosition.current = {
      top: buttonRect.bottom,
      left: buttonRect.left,
    };
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    // Clear any pending hover intent
    if (hoverIntentTimeout.current) {
      clearTimeout(hoverIntentTimeout.current);
      hoverIntentTimeout.current = null;
    }

    // Reset submenu state
    resetFacetSubmenu();

    // Close main menu
    setAnchorEl(null);
    anchorPosition.current = null;

    // Restore focus to the button after menu closes
    focusTimeout.current = setTimeout(() => {
      buttonRef.current?.focus();
      focusTimeout.current = null;
    }, 0);
  };

  const handleFacetSelect = (
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
    facet: FacetConfigWithKey,
  ) => {
    setSelectedFacet(facet);
    setFacetAnchorEl(event.currentTarget);

    // Pre-populate with existing filter values for this facet
    const existingClause = filterSet.find(
      (clause) => clause.facet === facet.key,
    );
    if (existingClause) {
      // Convert slugs to FacetOption objects (we'll have proper labels once options load)
      // Note: We use slug as a temporary id placeholder - the selectedValuesWithLabels
      // logic will match by slug to find the real option with the correct id
      const existingOptions: FacetOption[] = existingClause.values.map(
        (slug) => ({
          id: `temp-${slug}`, // Temporary placeholder id - real id will be resolved by slug lookup
          slug,
          label: slug, // Temporary label, will be replaced with proper label when options load
        }),
      );
      setSelectedValues(existingOptions);
    } else {
      setSelectedValues([]);
    }

    // Clear search state immediately (both UI and debounced state)
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPage(0);
  };

  // Check if mouse is within the "safe triangle" between menu item and submenu
  const isMouseInSafeTriangle = (
    mouseX: number,
    mouseY: number,
    menuItemRect: DOMRect,
  ): boolean => {
    if (!submenuBounds.current) return false;

    const submenu = submenuBounds.current;

    // Define triangle vertices:
    // Point 1: Top right corner of menu item
    // Point 2: Bottom right corner of menu item
    // Point 3: Closest corner of submenu (top-left or bottom-left)
    const p1 = { x: menuItemRect.right, y: menuItemRect.top };
    const p2 = { x: menuItemRect.right, y: menuItemRect.bottom };
    const p3 = {
      x: submenu.left,
      y:
        mouseY < menuItemRect.top + menuItemRect.height / 2
          ? submenu.top
          : submenu.bottom,
    };

    // Check if point is inside triangle using barycentric coordinates
    const sign = (
      px: number,
      py: number,
      ax: number,
      ay: number,
      bx: number,
      by: number,
    ) => {
      return (px - bx) * (ay - by) - (ax - bx) * (py - by);
    };

    const d1 = sign(mouseX, mouseY, p1.x, p1.y, p2.x, p2.y);
    const d2 = sign(mouseX, mouseY, p2.x, p2.y, p3.x, p3.y);
    const d3 = sign(mouseX, mouseY, p3.x, p3.y, p1.x, p1.y);

    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

    return !(hasNeg && hasPos);
  };

  const handleFacetHover = (
    event: MouseEvent<HTMLElement>,
    facet: FacetConfigWithKey,
  ) => {
    const currentMousePos = { x: event.clientX, y: event.clientY };

    // If hovering over a different facet than currently selected
    if (selectedFacet && selectedFacet.key !== facet.key) {
      // Check if mouse is in the safe triangle to the submenu
      const currentItemRect = event.currentTarget.getBoundingClientRect();

      // Check if mouse was previously inside the submenu - if so, ignore safe triangle
      const wasInsideSubmenu =
        submenuBounds.current && lastMousePos.current
          ? lastMousePos.current.x >= submenuBounds.current.left &&
            lastMousePos.current.x <= submenuBounds.current.right &&
            lastMousePos.current.y >= submenuBounds.current.top &&
            lastMousePos.current.y <= submenuBounds.current.bottom
          : false;

      // Update triangle visualization (debug mode only)
      if (DEBUG_SHOW_SAFE_TRIANGLE && submenuBounds.current) {
        const menuItem = currentItemRect;
        const submenu = submenuBounds.current;
        const mouseY = currentMousePos.y;

        const p1 = { x: menuItem.right, y: menuItem.top };
        const p2 = { x: menuItem.right, y: menuItem.bottom };
        const p3 = {
          x: submenu.left,
          y:
            mouseY < menuItem.top + menuItem.height / 2
              ? submenu.top
              : submenu.bottom,
        };

        setTrianglePoints(`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`);
      }

      // Only check safe triangle if we weren't just inside the submenu
      if (
        !wasInsideSubmenu &&
        isMouseInSafeTriangle(
          currentMousePos.x,
          currentMousePos.y,
          currentItemRect,
        )
      ) {
        lastMousePos.current = currentMousePos;
        return; // Don't switch items while in safe triangle
      }

      // Clear timeout immediately when moving to different item outside safe triangle
      if (hoverIntentTimeout.current) {
        clearTimeout(hoverIntentTimeout.current);
        hoverIntentTimeout.current = null;
      }
    } else {
      setTrianglePoints(null);
    }

    lastMousePos.current = currentMousePos;

    // If already hovering over the selected facet, don't restart the timer
    if (selectedFacet?.key === facet.key) {
      return;
    }

    // Clear any pending hover intent before creating new one
    if (hoverIntentTimeout.current) {
      clearTimeout(hoverIntentTimeout.current);
    }

    // Extract the target element before async operations
    const target = event.currentTarget;

    // Use shorter delay for responsive feel
    const delay = selectedFacet ? 150 : 100;
    hoverIntentTimeout.current = setTimeout(() => {
      const syntheticEvent = {
        currentTarget: target,
      } as MouseEvent<HTMLElement>;
      handleFacetSelect(syntheticEvent, facet);
    }, delay);
  };

  const handleFacetKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    facet: FacetConfigWithKey,
  ) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'ArrowRight'
    ) {
      event.preventDefault();

      // Clear any pending hover intent when keyboard is used
      if (hoverIntentTimeout.current) {
        clearTimeout(hoverIntentTimeout.current);
        hoverIntentTimeout.current = null;
      }

      // Reset mouse tracking state
      lastMousePos.current = null;
      submenuBounds.current = null;
      setTrianglePoints(null);

      handleFacetSelect(event, facet);
    }
  };

  const handleFacetMenuClose = () => {
    resetFacetSubmenu();
  };

  // Handle Left Arrow - go back to facet menu
  const handleLeftArrow = () => {
    resetFacetSubmenu();
    // Keep the first menu open, just close the autocomplete
    // The first menu item should regain focus automatically
  };

  // Handle Escape - close the entire menu
  const handleEscape = () => {
    handleClose();
  };

  const handleLoadMore = () => {
    if (paginatedOptions?.hasMore && !isLoading && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const handleValuesChange = (
    _event: SyntheticEvent,
    newValue: FacetOption[],
  ) => {
    setSelectedValues(newValue);

    // Auto-apply when values are selected
    if (newValue.length > 0 && selectedFacet) {
      const existingClause = filterSet.find(
        (clause) => clause.facet === selectedFacet.key,
      );

      const clause: FilterClause = {
        facet: selectedFacet.key,
        operator: existingClause?.operator || 'is', // Preserve existing operator
        values: newValue.map((v) => v.slug),
      };

      onAddFilter(clause);
    }
  };

  const handleCloseAfterSelection = () => {
    handleClose();
  };

  // Group facets by section and filter based on feature flags + page permissions
  const facetSections = useMemo(() => {
    return getFacetsBySection(config, features, {
      hideSingleSection: true,
      pagePermissions,
    });
  }, [config, features, pagePermissions]);

  return (
    <>
      {/* Debug visualization of safe triangle */}
      {DEBUG_SHOW_SAFE_TRIANGLE && trianglePoints && (
        <svg
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <polygon
            points={trianglePoints}
            fill="rgba(0, 255, 0, 0.2)"
            stroke="rgba(0, 255, 0, 0.5)"
            strokeWidth="2"
          />
        </svg>
      )}

      {filterSet.length > 0 ? (
        <IconButton
          id="add-filter-button"
          data-automation-id="add-filter-button"
          ref={buttonRef}
          onClick={handleButtonClick}
          size="tiny"
          tooltip={t('AddFilter')}
          iconName={uiIcons.controls.filter}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open}
        />
      ) : (
        <Button
          id="add-filter-button"
          data-automation-id="add-filter-button"
          ref={buttonRef}
          onClick={handleButtonClick}
          size="tiny"
          color="secondary"
          startIcon={<Icon name={uiIcons.controls.filter} aria-hidden="true" />}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {t('AddFilter')}
        </Button>
      )}

      {/* First level menu - Facet selection */}
      <Menu
        id={menuId}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorPosition.current
            ? {
                top: anchorPosition.current.top,
                left: anchorPosition.current.left,
              }
            : undefined
        }
        open={open}
        onClose={handleClose}
        aria-label={t('SelectAttribute')}
      >
        {facetSections.flatMap(({ section, label, facets }) => [
          // Only render section header if label exists (hidden for single-section case)
          ...(label
            ? [
                <ListSubheader
                  key={`header-${section}`}
                  disableGutters
                  component="div"
                >
                  {label}
                </ListSubheader>,
              ]
            : []),
          ...facets.map(({ key: facetKey, config: facet }) => (
            <MenuItem
              data-automation-id={`add-filter-menu-item-${facetKey}`}
              key={facetKey}
              onMouseEnter={(e) =>
                handleFacetHover(e, { ...facet, key: facetKey })
              }
              onKeyDown={(e) =>
                handleFacetKeyDown(e, { ...facet, key: facetKey })
              }
              onClick={(e) => handleFacetSelect(e, { ...facet, key: facetKey })}
              selected={selectedFacet?.key === facetKey}
              className={styles.FacetMenuItem}
              aria-haspopup="true"
              aria-expanded={selectedFacet?.key === facetKey}
            >
              <ListItemText>{facet.label}</ListItemText>
              <Icon
                name={uiIcons.navigation.next}
                aria-hidden="true"
                size="tiny"
              />
            </MenuItem>
          )),
        ])}
      </Menu>

      {/* Second level menu - Value selection with Autocomplete */}
      <Popover
        ref={popoverRef}
        open={!!selectedFacet}
        anchorEl={facetAnchorEl}
        onClose={handleFacetMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 11,
          horizontal: -9,
        }}
        slotProps={{
          paper: {
            className: styles.ValuePopoverPaper,
          },
          backdrop: {
            invisible: true,
          },
        }}
        className={styles.ValuePopover}
        transitionDuration={0}
      >
        {selectedFacet && (
          <FilterValueAutocomplete
            key={selectedFacet.key}
            autoFocus={true}
            options={visibleOptions}
            value={selectedValuesWithLabels}
            inputValue={searchTerm}
            onChange={handleValuesChange}
            onInputChange={(_, value, reason) => {
              // Only update search term when user is typing
              if (reason === 'input' || reason === 'clear') {
                setSearchTerm(value); // Update UI immediately
                debouncedSetSearch(value); // Debounced API call
              }
            }}
            onLoadMore={handleLoadMore}
            hasMore={paginatedOptions?.hasMore}
            loading={isLoading || isFetching}
            placeholder={t('SearchPlaceholder', {
              facet: selectedFacet?.label || t('Values'),
            })}
            showCounts={selectedFacet.facetBy !== false}
            onEscape={handleEscape}
            onLeftArrow={handleLeftArrow}
            onCloseAfterSelection={handleCloseAfterSelection}
            singleSelect={
              selectedFacet.singleSelect || selectedFacet.type === 'bool'
            }
            facetConfig={selectedFacet}
          />
        )}
      </Popover>
    </>
  );
};
